import LinearProgress from '@mui/material/LinearProgress';
import { CdnImage, Iconify } from '@mfw/components';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import React from 'react';

import { blobToBase64 } from '../../utils';
import { onSurface } from '../../theme/onSurface';
import './UploadProgress.scss';

export interface UploadProgressProps {
  /**
   * File from which to pull thumbnail image.
   */
  file?: File | null | string;
  /**
   * Callback triggered when remove button is clicked.
   */
  onRemove?: () => void;
  description?: string;
  /**
   * Number of percentage
   */
  progress?: number;
  /**
   * The upload STOPPED and did not finish.
   *
   * Without it a failed upload is indistinguishable from a live one: the row
   * simply freezes at whatever percentage the network reached, which reads as
   * "still going" for as long as the user is willing to wait. The worker has
   * always been able to report the failure — nothing rendered it.
   */
  error?: boolean;
  /** Shown in place of the progress bar when `error` is set. */
  errorLabel?: string;
  title: string;
}

const UploadProgress = ({
  description,
  error,
  errorLabel,
  file,
  onRemove,
  progress,
  title,
}: UploadProgressProps) => {
  const [url, setUrl] = React.useState(typeof file === 'string' ? file : '');

  React.useEffect(() => {
    if (file && typeof file !== 'string') {
      blobToBase64(file).then((res) => {
        if (!res) return;
        setUrl(res);
      });
    }
  }, [file]);
  return (
    <div className="Mfw-UploadProgress-root">
      <CdnImage
        className="Mfw-UploadProgress-image"
        rounded={false}
        alt={title}
        src={url}
      />
      <div className="Mfw-UploadProgress-content">
        <Typography className="Mfw-UploadProgress-title">{title}</Typography>
        <Typography
          className="Mfw-UploadProgress-description"
          variant="caption"
        >
          ({description})
        </Typography>
        {error ? (
          <Typography color={onSurface.error} variant="caption">
            {errorLabel}
          </Typography>
        ) : (
          <LinearProgress variant="determinate" value={progress} />
        )}
      </div>
      <div className="Mfw-UploadProgress-actions">
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            onRemove?.();
          }}
        >
          <Iconify icon="mdi:trash" size="medium" />
        </IconButton>
      </div>
    </div>
  );
};

export default UploadProgress;
